import type { PostHog } from "posthog-js";
import { type PropsWithChildren, useEffect, useState } from "react";
import { AllFeatureFlags } from "../allFeatureFlags/AllFeatureFlags";
import { FeatureFlagContext } from "../FeatureFlagContext";
import { StaticFeatureFlags } from "../staticFeatureFlags/StaticFeatureFlags";
import { PosthogFeatureFlags } from "./PosthogFeatureFlags";

interface Options<Flags> {
  posthog: PostHog | null;
  flags?: { [key in keyof Flags]: boolean };
}

declare global {
  var overrideFeatureFlags: (flags: Record<string, boolean>) => void;
}

export function createPosthogFeatureFlagProvider<Flags>({
  posthog,
  flags,
}: Options<Flags>) {
  // If feature flags are set in the config, use them instead of PostHog's feature flags
  if (flags) {
    return function StaticFeatureFlagsProvider({
      children,
    }: PropsWithChildren) {
      return (
        <FeatureFlagContext.Provider
          value={new StaticFeatureFlags(flags, { fallback: true })}
        >
          {children}
        </FeatureFlagContext.Provider>
      );
    };
  }

  if (!posthog) {
    return function AllFeatureFlagsProvider({ children }: PropsWithChildren) {
      return (
        <FeatureFlagContext.Provider value={new AllFeatureFlags()}>
          {children}
        </FeatureFlagContext.Provider>
      );
    };
  }

  // Allow setting feature flags from the console
  window.overrideFeatureFlags = (flags: Record<string, boolean>) => {
    posthog.featureFlags.overrideFeatureFlags({ flags });
    // biome-ignore lint/suspicious/noConsole: Used to show data locally in the browser
    console.table(
      Object.fromEntries(
        posthog.featureFlags
          .getFlags()
          .map((flag) => [flag, posthog.getFeatureFlag(flag)])
      )
    );
    posthog.featureFlags.reloadFeatureFlags();
  };

  return function PosthogFeatureFlagsProvider({ children }: PropsWithChildren) {
    const [hasLoadedFlags, setHasLoadedFlags] = useState(false);

    useEffect(() => {
      posthog.onFeatureFlags(() => {
        setHasLoadedFlags(true);
      });

      const interval = setInterval(() => {
        posthog.featureFlags.reloadFeatureFlags();
      }, 1000);

      return () => clearInterval(interval);
    }, []);

    if (!hasLoadedFlags) {
      return null;
    }

    return (
      <FeatureFlagContext.Provider value={new PosthogFeatureFlags(posthog)}>
        {children}
      </FeatureFlagContext.Provider>
    );
  };
}
